import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Sparkles, Dog, Briefcase, ChevronDown,
  PawPrint, ImagePlus, MapPin, Phone, Globe2, MessageSquare, Layers, Search,
} from 'lucide-react';
import usePageSEO from '../hooks/usePageSEO';

const SITE = 'https://apisai.pl';
const ACCENT = '#F97316';

const CONTENT = {
  hodowla: {
    path: '/strona-dla-hodowli',
    typeLabel: 'Dla hodowli psów',
    Icon: Dog,
    title: 'Darmowa strona internetowa dla hodowli psów — kreator AI | ApisAI',
    description: 'Stwórz profesjonalną stronę hodowli w kilka minut z AI. Prezentuj psy, mioty i reproduktory, zdobywaj zapytania. Bez logowania na start — publikujesz, kiedy chcesz.',
    h1: 'Darmowa strona internetowa dla hodowli psów',
    lead: 'Stwórz profesjonalną stronę hodowli w kilka minut z pomocą AI — bez logowania na start. Prezentuj psy, mioty i reproduktory, zdobywaj zapytania od kupujących. Publikujesz dopiero wtedy, kiedy chcesz.',
    appName: 'APISAI — Kreator strony dla hodowli',
    mock: { url: 'apisai.pl/hodowla/twoja-hodowla', title: 'Złote Marzenia FCI', tag: 'Golden Retriever • Warszawa', about: 'Rodzinna hodowla z linii wystawowych. Psy z badaniami zdrowia i pełną dokumentacją ZKwP.', chips: ['Golden Retriever'], galleryLabel: 'Galeria' },
    steps: [
      ['Odpowiedz na kilka pytań', 'Podaj nazwę hodowli, rasy i miasto. AI przygotuje opis i układ strony za Ciebie.'],
      ['Zobacz gotowy podgląd', 'Twoja strona powstaje na żywo — bez logowania i bez podawania karty.'],
      ['Opublikuj i zdobywaj klientów', 'Załóż konto, dodaj psy, mioty i zdjęcia, a następnie opublikuj profil w katalogu APISAI.'],
    ],
    benefits: [
      [Layers, 'Wszystko w jednym miejscu', 'Strona, katalog i zapytania od klientów bez przeskakiwania między narzędziami.'],
      [Search, 'Widoczność w Google', 'Opublikowana strona i profil mogą być indeksowane oraz widoczne w katalogu hodowli APISAI.'],
      [PawPrint, 'Prezentacja psów i miotów', 'Pokaż suki, reproduktory i aktualne mioty ze zdjęciami i opisem.'],
      [Sparkles, 'Bez agencji, bez umów', 'Start całkowicie za darmo. Treści tworzy AI — Ty tylko akceptujesz.'],
    ],
    faq: [
      ['Czy stworzenie strony dla hodowli jest darmowe?', 'Tak. Wygenerowanie strony i podglądu jest darmowe i nie wymaga logowania. Konto zakładasz dopiero wtedy, gdy chcesz zapisać i opublikować stronę.'],
      ['Czy muszę znać się na technologii?', 'Nie. Odpowiadasz na kilka prostych pytań, a AI przygotowuje treści oraz układ strony hodowli.'],
      ['Co znajdzie się na stronie hodowli?', 'Opis hodowli, rasy, galeria, prezentacja psów, miotów i reproduktorów oraz formularz zapytania i dane kontaktowe.'],
      ['Czy strona pojawi się w Google?', 'Tak. Opublikowana strona oraz profil mogą być indeksowane w Google i widoczne w katalogu hodowli APISAI.'],
      ['Czy mogę edytować stronę później?', 'Tak. Po zalogowaniu w panelu w każdej chwili dodasz psy, mioty i zdjęcia oraz zmienisz dowolne treści.'],
    ],
    related: [['/hodowle', 'Katalog hodowli psów'], ['/rasy', 'Rasy psów'], ['/strona-dla-firmy', 'Strona dla lokalnej firmy']],
    more: { href: '/dla-hodowcy', label: 'Dowiedz się więcej o APISAI dla hodowców' },
  },
  firma: {
    path: '/strona-dla-firmy',
    typeLabel: 'Dla lokalnych firm',
    Icon: Briefcase,
    title: 'Darmowa strona i wizytówka dla lokalnej firmy — kreator AI | ApisAI',
    description: 'Stwórz stronę dla firmy w kilka minut z AI. Groomer, hotel dla psów, szkoleniowiec i inne usługi. Bez logowania na start — publikujesz, kiedy chcesz.',
    h1: 'Darmowa strona i wizytówka dla lokalnej firmy',
    lead: 'Stwórz stronę dla swojej firmy w kilka minut z pomocą AI — bez logowania na start. Groomer, hotel dla psów, szkoleniowiec czy inna lokalna usługa — prezentuj ofertę i zdobywaj klientów. Publikujesz dopiero wtedy, kiedy chcesz.',
    appName: 'APISAI — Kreator strony dla firmy',
    mock: { url: 'apisai.pl/firma/twoja-firma', title: 'Pies Pomarańczowy', tag: 'Groomer • Kraków', about: 'Strzyżenie i pielęgnacja psów wszystkich ras. Delikatne podejście i pełne portfolio realizacji.', chips: ['Strzyżenie', 'Kąpiel', 'Trymowanie'], galleryLabel: 'Realizacje' },
    steps: [
      ['Odpowiedz na kilka pytań', 'Podaj nazwę firmy, branżę i miasto. AI przygotuje opis i układ strony.'],
      ['Zobacz gotowy podgląd', 'Strona powstaje na żywo — bez logowania i bez podawania karty.'],
      ['Opublikuj i zdobywaj klientów', 'Załóż konto, dodaj usługi i zdjęcia, a następnie opublikuj wizytówkę w katalogu APISAI.'],
    ],
    benefits: [
      [Layers, 'Wszystko w jednym miejscu', 'Strona, katalog lokalny i zapytania od klientów w jednym panelu.'],
      [Search, 'Widoczność w Google', 'Opublikowana strona i profil mogą być indeksowane oraz widoczne w katalogu firm APISAI.'],
      [Briefcase, 'Prezentacja usług', 'Pokaż ofertę, cennik i realizacje ze zdjęciami — tak, jak chcą je widzieć klienci.'],
      [Sparkles, 'Bez agencji, bez umów', 'Start całkowicie za darmo. Treści tworzy AI — Ty tylko akceptujesz.'],
    ],
    faq: [
      ['Czy stworzenie strony dla firmy jest darmowe?', 'Tak. Wygenerowanie strony i podglądu jest darmowe i nie wymaga logowania. Konto zakładasz dopiero wtedy, gdy chcesz zapisać i opublikować stronę.'],
      ['Dla jakich firm to działa?', 'Dla lokalnych usług: groomerzy, hotele dla psów, szkoleniowcy, handlerzy, weterynarze, sklepy zoologiczne i inne firmy usługowe.'],
      ['Co znajdzie się na stronie firmy?', 'Opis firmy, oferta usług, galeria realizacji, lokalizacja oraz formularz zapytania i dane kontaktowe.'],
      ['Czy strona pojawi się w Google?', 'Tak. Opublikowana strona oraz profil mogą być indeksowane w Google i widoczne w katalogu firm APISAI.'],
      ['Czy mogę edytować stronę później?', 'Tak. Po zalogowaniu w panelu w każdej chwili dodasz usługi i zdjęcia oraz zmienisz dowolne treści.'],
    ],
    related: [['/katalog', 'Katalog lokalnych firm'], ['/hodowle', 'Katalog hodowli psów'], ['/strona-dla-hodowli', 'Strona dla hodowli psów']],
    more: { href: '/dla-firm-uslugowych', label: 'Dowiedz się więcej o APISAI dla firm' },
  },
};

function buildSchemas(c) {
  const canonical = `${SITE}${c.path}`;
  return [
    {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Strona główna', item: SITE },
        { '@type': 'ListItem', position: 2, name: c.h1, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org', '@type': 'SoftwareApplication',
      name: c.appName, applicationCategory: 'BusinessApplication', operatingSystem: 'Web',
      url: canonical, description: c.description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'PLN' },
    },
    {
      '@context': 'https://schema.org', '@type': 'HowTo', name: c.h1,
      step: c.steps.map(([name, text], i) => ({ '@type': 'HowToStep', position: i + 1, name, text })),
    },
    {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: c.faq.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
    },
  ];
}

function buildDynamic(variant, name, count, slug) {
  const base = CONTENT[variant];
  if (variant === 'hodowla') {
    const social = count > 0 ? ` Dołącz do ${count} hodowli rasy ${name} obecnych w APISAI.` : '';
    return {
      ...base,
      path: `/strona-dla-hodowli/${slug}`,
      count,
      title: `Darmowa strona dla hodowli rasy ${name} — kreator AI | ApisAI`,
      description: `Stwórz stronę dla hodowli rasy ${name} w kilka minut z AI. Prezentuj psy, mioty i reproduktory, zdobywaj zapytania. Bez logowania na start.`,
      h1: `Darmowa strona dla hodowli rasy ${name}`,
      lead: `Prowadzisz hodowlę rasy ${name}? Stwórz profesjonalną stronę w kilka minut z pomocą AI — bez logowania na start. Prezentuj psy, mioty i reproduktory, zdobywaj zapytania od kupujących.${social}`,
      appName: `APISAI — Kreator strony dla hodowli rasy ${name}`,
      mock: { ...base.mock, title: `Hodowla ${name}`, tag: `${name} • Twoje miasto`, chips: [name] },
      faq: [[`Czy strona sprawdzi się dla hodowli rasy ${name}?`, `Tak. Szablon jest dopasowany do hodowli psów rasowych — wyeksponujesz rasę ${name}, psy, mioty i reproduktory oraz zbierzesz zapytania od kupujących.`], ...base.faq.slice(0, 4)],
      related: [[`/rasy/${slug}`, `Rasa ${name} — opis i hodowle`], ['/hodowle', 'Katalog hodowli psów'], ['/strona-dla-hodowli', 'Strona dla hodowli (ogólna)']],
    };
  }
  const social = count > 0 ? ` Dołącz do ${count} firm z kategorii ${name} w katalogu APISAI.` : '';
  return {
    ...base,
    path: `/strona-dla-firmy/${slug}`,
    count,
    title: `Darmowa strona internetowa dla firmy — ${name} | ApisAI`,
    description: `Stwórz stronę dla firmy z kategorii ${name} w kilka minut z AI. Pokaż ofertę i zdobywaj klientów lokalnie. Bez logowania na start.`,
    h1: `Darmowa strona internetowa dla firmy — ${name}`,
    lead: `Prowadzisz firmę z kategorii ${name}? Stwórz profesjonalną stronę w kilka minut z pomocą AI — bez logowania na start. Pokaż ofertę, cennik i realizacje, zdobywaj klientów lokalnie.${social}`,
    appName: `APISAI — Kreator strony dla firmy (${name})`,
    mock: { ...base.mock, title: name, tag: `${name} • Twoje miasto`, about: 'Profesjonalna oferta, cennik i realizacje. Formularz zapytania i dane kontaktowe w jednym miejscu.', chips: ['Oferta', 'Cennik', 'Kontakt'] },
    faq: [[`Czy strona sprawdzi się dla kategorii ${name}?`, 'Tak. Szablon dopasujesz do swojej oferty — pokaż usługi, cennik i realizacje oraz zbieraj zapytania od klientów.'], ...base.faq.slice(0, 4)],
    related: [[`/katalog/${slug}`, `Katalog: ${name}`], ['/katalog', 'Katalog lokalnych firm'], ['/strona-dla-firmy', 'Strona dla firmy (ogólna)']],
  };
}

const MockPreview = ({ mock, Icon }) => (
  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white" data-testid="seo-mock-preview">
    <div className="flex items-center gap-2 px-3 py-2.5 bg-[#0f1e2a] border-b border-white/10">
      <span className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
      </span>
      <div className="flex-1 mx-1.5 h-6 rounded-md bg-white/[0.06] border border-white/10 flex items-center gap-1.5 px-2.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="text-[11px] text-slate-400 truncate">{mock.url}</span>
      </div>
    </div>
    <div className="bg-white text-slate-900">
      <div className="relative h-40 flex items-end" style={{ background: 'linear-gradient(135deg, #00273D 0%, #013a52 55%, #F97316 210%)' }}>
        <Icon className="w-16 h-16 text-white/10 absolute top-4 right-4" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.05) 60%)' }} />
        <div className="relative p-5">
          <h3 className="text-xl font-extrabold text-white leading-tight drop-shadow">{mock.title}</h3>
          <span className="inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ background: 'rgba(249,115,22,0.9)' }}>{mock.tag}</span>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">O nas</div>
          <p className="text-sm leading-relaxed text-slate-700">{mock.about}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {mock.chips.map((ch) => <span key={ch} className="px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">{ch}</span>)}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">{mock.galleryLabel}</div>
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-square rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                <ImagePlus className="w-5 h-5 text-slate-300" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" style={{ color: ACCENT }} /> Twoje miasto</span>
          <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" style={{ color: ACCENT }} /> Kontakt</span>
        </div>
      </div>
    </div>
  </div>
);

export default function SeoCreatorLanding({ variant = 'hodowla' }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dyn, setDyn] = useState(null);
  const [status, setStatus] = useState(slug ? 'loading' : 'ready');
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!slug) { setStatus('ready'); setDyn(null); return; }
    let alive = true;
    setStatus('loading');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/creator-landing?type=${variant}&slug=${encodeURIComponent(slug)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        if (d && d.ok) { setDyn({ name: d.name, count: d.count }); setStatus('ready'); }
        else { setStatus('notfound'); }
      })
      .catch(() => { if (alive) setStatus('notfound'); });
    return () => { alive = false; };
  }, [slug, variant]);

  useEffect(() => {
    if (status === 'notfound') {
      navigate(variant === 'hodowla' ? '/strona-dla-hodowli' : '/strona-dla-firmy', { replace: true });
    }
  }, [status, variant, navigate]);

  const c = (slug && dyn) ? buildDynamic(variant, dyn.name, dyn.count, slug) : (CONTENT[variant] || CONTENT.hodowla);

  usePageSEO({
    title: c.title,
    description: c.description,
    canonical: `${SITE}${c.path}`,
    ogTitle: c.h1,
    ogDescription: c.description,
    ogSiteName: 'ApisAI',
    schemas: buildSchemas(c),
  });

  if (slug && status !== 'ready') {
    return (
      <div className="min-h-screen bg-[#070710] flex items-center justify-center text-zinc-400" data-testid="seo-landing-loading">
        <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 animate-pulse" /> Ładowanie…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070710] text-white antialiased" data-testid={`seo-landing-${variant}`}>
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070710]/85 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-extrabold text-lg tracking-tight" data-testid="seo-nav-logo">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: ACCENT }}><PawPrint className="w-5 h-5" /></span>
            APISAI
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/logowanie" className="hidden sm:inline text-sm font-medium text-zinc-300 hover:text-white transition">Zaloguj się</Link>
            <Link to="/stworz" data-testid="seo-nav-cta"
              className="text-sm font-semibold px-4 py-2 rounded-lg text-white transition shadow-lg shadow-orange-500/20"
              style={{ background: ACCENT }}>
              Wygeneruj stronę za darmo
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: ACCENT }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/[0.06] border border-white/10 text-orange-200">
              <c.Icon className="w-3.5 h-3.5" /> {c.typeLabel}
            </span>
            {c.count > 0 && (
              <span className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/10 border border-emerald-500/20 text-emerald-300" data-testid="seo-social-proof">
                {c.count} {variant === 'hodowla' ? 'hodowli w APISAI' : 'firm w APISAI'}
              </span>
            )}
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">{c.h1}</h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">{c.lead}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/stworz" data-testid="seo-hero-cta"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/30 transition-transform hover:-translate-y-0.5"
                style={{ background: ACCENT }}>
                Wygeneruj swoją stronę za darmo <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to={c.more.href}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-zinc-200 border border-white/15 hover:bg-white/5 transition">
                Dowiedz się więcej
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-300">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Bez logowania na start</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Gotowy podgląd w kilka kroków</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Strona, katalog i zapytania w jednym miejscu</span>
            </div>
          </div>
          <div className="relative">
            <MockPreview mock={c.mock} Icon={c.Icon} />
          </div>
        </div>
      </section>

      {/* JAK TO DZIAŁA */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="seo-how">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center">Jak to działa — w 3 krokach</h2>
          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {c.steps.map(([name, text], i) => (
              <div key={name} className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <span className="absolute -top-4 left-6 w-9 h-9 rounded-xl flex items-center justify-center font-extrabold text-white" style={{ background: ACCENT }}>{i + 1}</span>
                <h3 className="mt-3 text-lg font-bold">{name}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KORZYŚCI */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="seo-benefits">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center">Co zyskujesz</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {c.benefits.map(([Icon, title, text]) => (
              <div key={title} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-orange-500/15 text-orange-300">
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-base font-bold">{title}</h3>
                  <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="seo-faq">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center">Najczęstsze pytania</h2>
          <div className="mt-10 space-y-3">
            {c.faq.map(([q, a], i) => (
              <div key={q} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`seo-faq-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-semibold text-[15px]">{q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 text-zinc-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed">{a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24 border-t border-white/5" data-testid="seo-final-cta">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
            {variant === 'hodowla' ? 'Pokaż swoją hodowlę.' : 'Pokaż swoją firmę.'}<br />
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">Zdobądź więcej zapytań.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-xl mx-auto">
            Stwórz stronę za darmo, zobacz podgląd i zdecyduj później, czy chcesz ją opublikować.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/stworz" data-testid="seo-final-cta-btn"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/30 transition-transform hover:-translate-y-0.5"
              style={{ background: ACCENT }}>
              Wygeneruj stronę za darmo <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER / LINKOWANIE WEWNĘTRZNE */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 font-extrabold text-lg">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: ACCENT }}><PawPrint className="w-5 h-5" /></span>
            APISAI
          </Link>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
            {c.related.map(([href, label]) => (
              <Link key={href} to={href} className="hover:text-white transition" data-testid={`seo-related-${href.replace(/^\//, '').replace(/\//g, '-')}`}>{label}</Link>
            ))}
            <Link to="/kontakt" className="hover:text-white transition">Kontakt</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
